import { motion } from "framer-motion";
import { AlertTriangle, TrendingDown, Eye, DollarSign } from "lucide-react";

const problems = [
  {
    icon: DollarSign,
    stat: "$20bn+",
    title: "Wasted annually",
    description: "On technology acquisitions that fail to deliver promised value due to undiscovered issues.",
  },
  {
    icon: TrendingDown,
    stat: "73%",
    title: "Of tech acquisitions underperform",
    description: "Due to undiscovered technical debt, integration complexity, or misrepresented capabilities.",
  },
  {
    icon: Eye,
    stat: "1 in 3",
    title: "'AI-powered' products are wrappers",
    description: "Claims of proprietary technology frequently mask thin layers over open-source tools.",
  },
];

const failedDeals = [
  {
    buyer: "PayPal",
    target: "Honey",
    value: "$4bn",
    issue: "Dark-pattern fraud discovered post-close",
  },
  {
    buyer: "HP",
    target: "Autonomy",
    value: "$11bn",
    issue: "$8.8bn write-down from misrepresented tech",
  },
  {
    buyer: "Verizon",
    target: "Yahoo",
    value: "$4.5bn",
    issue: "Undisclosed breaches, $350M price cut",
  },
  {
    buyer: "Uber",
    target: "Postmates",
    value: "$2.65bn",
    issue: "Integration nightmare, platform redundancy",
  },
];

export const WhyProvenance = () => {
  return (
    <section className="relative py-24 bg-card/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            The problem
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            Technology acquisitions are failing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Buyers pay billions for technology assets they don't truly understand. 
            The consequences are devastating and increasingly public.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative rounded-2xl border border-destructive/30 bg-destructive/5 p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                  <problem.icon className="h-6 w-6 text-destructive" />
                </div>
                <span className="font-display text-3xl font-bold text-gradient">
                  {problem.stat}
                </span>
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">
                {problem.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Failed Deals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h3 className="font-display text-xl font-semibold text-center mb-8 flex items-center justify-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            High-profile failures
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {failedDeals.map((deal, idx) => (
              <motion.div
                key={deal.target}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + idx * 0.1 }}
                className="rounded-xl border border-border bg-background p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{deal.buyer}</span>
                  <span className="text-xs font-bold text-primary">{deal.value}</span>
                </div>
                <h4 className="font-display font-bold mb-2">{deal.target}</h4>
                <p className="text-xs text-destructive">{deal.issue}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <p className="text-lg font-medium text-foreground">
            PROVENANCE exists to close the gap between{" "}
            <span className="text-gradient">what you're told</span> and{" "}
            <span className="text-gradient">what you're buying</span>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
